import { createContext, useContext, useState, useCallback } from 'react';
import Modal from '../components/Modal';
import QuoteForm from '../components/forms/QuoteForm';

const FormsContext = createContext(null);

/**
 * FormsProvider
 *
 * Renders the Quote modal at the app root so it is accessible from any page,
 * and exposes openQuote() via the FormsContext. (The wholesale application is
 * a dedicated page at /wholesale, not a modal.)
 */
export function FormsProvider({ children }) {
  // --- Quote modal state ---
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteProduct, setQuoteProduct] = useState({ productId: null, productName: null });

  // Stable callbacks so consumers don't re-render on every provider render
  const openQuote = useCallback(({ productId = null, productName = null } = {}) => {
    setQuoteProduct({ productId, productName });
    setQuoteOpen(true);
  }, []);

  const closeQuote = useCallback(() => {
    setQuoteOpen(false);
    // Reset product context after the close animation settles
    setTimeout(() => setQuoteProduct({ productId: null, productName: null }), 250);
  }, []);

  return (
    <FormsContext.Provider value={{ openQuote }}>
      {children}

      {/* Quote Request Modal */}
      <Modal
        open={quoteOpen}
        onClose={closeQuote}
        title="Request a Quote"
      >
        {quoteOpen && (
          <QuoteForm
            productId={quoteProduct.productId}
            productName={quoteProduct.productName}
            onSuccess={closeQuote}
          />
        )}
      </Modal>
    </FormsContext.Provider>
  );
}

/**
 * useForms() — consume openQuote from any component.
 */
export function useForms() {
  const ctx = useContext(FormsContext);
  if (!ctx) {
    throw new Error('useForms must be used inside <FormsProvider>');
  }
  return ctx;
}
